/* @flow */

import React from 'react';
import styled from 'styled-components';
import {
  Input,
  Select,
  Message,
  Tooltip,
  // $FlowFixMe
} from '@bijie/react-bnb';
import Icon from '../Icon';
import { getLabel } from '../../utils/i18n';
import {
  bigNumberMultiply, bigNumberMinus, bigNumberDivided, decRound,
} from '../../utils/number';
import { amountValidate } from '../../utils/validate';
import {
  Line,
  Label,
  TipBox,
  ErrorMsg,
  ErrorLine,
  filedRules,
  FooterComp,
  CoinOption,
  StyledSelect,
  NUMBER_ONLY_REGREX,
} from './TransferModal';
import {
  marginRepay,
  marginBorrow,
} from '../../actions/actionsSet/margin';

const Container = styled.div`
  width: 50%;
  padding: 0 40px;
  .wrapper {
    position: relative;
    height: 100%;
    padding-bottom: 80px;
  }
`;
const Desc = styled.p`
  margin-bottom: 7px;
  line-height: 18px;
  color: #848E9C;
  &:last-child {
    margin-bottom: 0;
  }
`;
const DescValue = styled.span`
  float: right;
  color: #48515D;
  font-weight: 500;
`;
const PercentContainer = styled.ul`
  display: flex;
  justify-content: space-between;
  list-style: none;
  color: #48515D;
`;
const PercentItem = styled.li`
  padding: 5px 25px;
  line-height: 14px;
  border: 1px solid #eaecef;
  border-radius: 13px;
  cursor: pointer;
`;
const RatingBox = styled.span`
  margin-left: 5px;
  cursor: pointer;
  .icon-prompt {
    &:hover { color: #f0b90b; }
  }
`;
const OverlayBox = styled.div`
  max-width: 400px;
`;
const RatingTip = ({ i18n }: Object) => (
  <RatingBox>
    <Tooltip placement="top" trigger={['hover']} overlay={<OverlayBox>{getLabel('exchange-MarginBalanceDetail-ratedesc', i18n)}</OverlayBox>}>
      <Icon type="prompt" size={14} color="#48515D" />
    </Tooltip>
  </RatingBox>
);

const allWidth = { width: '100%' };

type Props = {
  i18n: Object,
  maxAmount: string,
  panelType: string,
  userAssets: Array<Object>,
  initAsset: Object,
  closeModal: Function,
  handleSelectChange: Function
}
type States = {
  amount: string,
  errorMsg: string,
  isFetching: bool,
}
export default class BorrowPanel extends React.PureComponent<Props, States> {
  state = {
    amount: '0',
    errorMsg: '',
    isFetching: false,
  }

  _handleInputChange = (value: string) => {
    if (!NUMBER_ONLY_REGREX.test(value) && value.length !== 0) {
      return undefined;
    }
    return this.setState({
      amount: value,
    });
  }

  _handleSelectChange = (asset: string) => {
    const assetObj = this.props.userAssets.find(symbol => symbol.asset === asset);
    if (!assetObj) {
      return;
    }
    this.setState({
      amount: '0',
      errorMsg: '',
    });
    const { panelType, handleSelectChange } = this.props;
    handleSelectChange(panelType, assetObj);
  }

  _setAmount = (percent: string) => {
    const { maxAmount } = this.props;
    const amount = bigNumberMultiply(maxAmount, percent);
    this.setState({
      amount,
    });
  }

  _hanldeTipClick = () => {
    this._setAmount('1');
  }

  _confirm = async () => {
    const validate = this._validate();
    if (!validate) {
      return;
    }
    this.setState({
      isFetching: true,
    });
    const { amount } = this.state;
    const {
      i18n,
      panelType,
      initAsset: { asset },
    } = this.props;
    const param = {
      asset,
      amount,
    };
    let reload = false;
    try {
      if (panelType === 'Borrow') {
        await marginBorrow(param);
        Message.success(getLabel('exchange-BorrowPanel-requestApproved', i18n));
      } else {
        await marginRepay(param);
        Message.success(getLabel('exchange-BorrowPanel-repaySuccessful', i18n));
      }
      reload = true;
    } catch (error) {
      const message = error.message.split(',').reduce((cur, next) => cur + getLabel(next, i18n), '');
      Message.fail(message);
    } finally {
      this.setState({
        isFetching: false,
      });
      this.props.closeModal(reload);
    }
  }

  renderBorrowList = () => {
    const {
      i18n,
      maxAmount,
      initAsset: { borrowed, asset, interestRate },
    } = this.props;
    return (
      <Line>
        <Desc>
          <span>{getLabel('exchange-common-hr-interestrate', i18n)}:</span><RatingTip i18n={i18n} />
          <DescValue>{decRound(bigNumberDivided(interestRate, '24', 10) * 100, 8)}%</DescValue>
        </Desc>
        <Desc>
          <span>{getLabel('exchange-common-borrowed', i18n)}:</span>
          <DescValue>{borrowed}&nbsp;{asset}</DescValue>
        </Desc>
        <Desc>
          <span>{getLabel('exchange-BorrowPanel-maximumBorrowAmount', i18n)}:</span>
          <DescValue>{maxAmount}&nbsp;{asset}</DescValue>
        </Desc>
      </Line>
    );
  }

  renderRepayList = () => {
    const {
      i18n,
      initAsset: {
        asset,
        free,
        interest,
        borrowed,
        totalDebt,
      },
    } = this.props;
    return (
      <Line padding="18">
        <Desc>
          <span>{getLabel('exchange-BorrowPanel-availableBalance', i18n)}:</span>
          <DescValue>{free}&nbsp;{asset}</DescValue>
        </Desc>
        <Desc>
          <span>{getLabel('exchange-common-interest', i18n)}&nbsp;(A):</span><RatingTip i18n={i18n} />
          <DescValue>{interest}&nbsp;{asset}</DescValue>
        </Desc>
        <Desc>
          <span>{getLabel('exchange-common-borrowed', i18n)}&nbsp;(B):</span>
          <DescValue>{borrowed}&nbsp;{asset}</DescValue>
        </Desc>
        <Desc>
          <span>{getLabel('exchange-MarginBalanceDetail-TotalDebt', i18n)}&nbsp;(A + B):</span>
          <DescValue>{totalDebt}&nbsp;{asset}</DescValue>
        </Desc>
      </Line>
    );
  }

  renderAmount = () => {
    const {
      i18n,
      maxAmount,
      panelType,
      initAsset: { isBorrowable, borrowed, asset },
    } = this.props;
    const isBorrow = panelType === 'Borrow';
    const { amount, errorMsg } = this.state;
    if ((isBorrow && !isBorrowable) || (!isBorrow && borrowed <= 0)) {
      let label = getLabel('exchange-BorrowPanel-suspensionOfLoan', i18n);
      !isBorrow && borrowed <= 0 && (label = getLabel('exchange-BorrowPanel-haventBorrowed', i18n));
      return (
        <ErrorLine
          asset={asset}
          msg={getLabel(label, i18n)}
        />
      );
    }
    return (
      <React.Fragment>
        <Line padding={isBorrow ? '30' : '17'}>
          <Label>{getLabel('exchange-common-amount', i18n)}</Label>
          <Input
            value={amount}
            style={allWidth}
            onBlur={this._validate}
            onChange={this._handleInputChange}
          />
          <ErrorMsg>
            {errorMsg}
          </ErrorMsg>
          <TipBox onClick={this._hanldeTipClick}>
            {/* eslint-disable-next-line max-len */}
            <Label>{getLabel(`${isBorrow ? 'exchange-BorrowPanel-available' : 'exchange-BorrowPanel-maxrepay'}`, i18n)}:</Label>
            &nbsp;&nbsp;{maxAmount}&nbsp;&nbsp;{asset}
          </TipBox>
        </Line>
        <Line padding="40">
          <PercentContainer>
            <PercentItem onClick={() => this._setAmount('0.25')}>25%</PercentItem>
            <PercentItem onClick={() => this._setAmount('0.5')}>50%</PercentItem>
            <PercentItem onClick={() => this._setAmount('0.75')}>75%</PercentItem>
            <PercentItem onClick={() => this._setAmount('1')}>100%</PercentItem>
          </PercentContainer>
        </Line>
      </React.Fragment>
    );
  }

  validateBorrow = () => {
    const { amount } = this.state;
    const { i18n, maxAmount, initAsset: { userMinBorrow } } = this.props;
    const { validate, errorMsg } = amountValidate(amount, filedRules, i18n, 'borrow', maxAmount, userMinBorrow);
    this.setState({
      errorMsg,
    });
    return validate;
  }

  validateRepay = () => {
    const { amount } = this.state;
    const { i18n, maxAmount, initAsset: { free, userMinBorrow, userMinRepay } } = this.props;
    const { validate, errorMsg } = amountValidate(amount, filedRules, i18n, 'repay', free, maxAmount, bigNumberMinus(maxAmount, userMinBorrow), userMinRepay);
    this.setState({
      errorMsg,
    });
    return validate;
  }

  _validate = () => {
    const { panelType } = this.props;
    let validate;
    if (panelType === 'Borrow') {
      validate = this.validateBorrow();
    } else {
      validate = this.validateRepay();
    }
    return validate;
  }

  render() {
    const {
      i18n,
      panelType,
      userAssets,
      initAsset,
    } = this.props;
    const { isFetching } = this.state;
    const isBorrow = panelType === 'Borrow';
    const disable = (isBorrow && !initAsset.isBorrowable) || (!isBorrow && initAsset.borrowed <= 0);
    return (
      <Container>
        <div className="wrapper">
          <Line padding="0">
            <Label>{getLabel('exchange-common-coin', i18n)}</Label>
            <StyledSelect
              value={<CoinOption {...initAsset} />}
              defaultActiveFirstOption={false}
              showSearch={false}
              style={allWidth}
              onChange={this._handleSelectChange}
            >
              {
              userAssets.map(({ asset, assetName, logoUrl }) => (
                <Select.Option value={asset} key={asset}>
                  <CoinOption asset={asset} assetName={assetName} logoUrl={logoUrl} />
                </Select.Option>
              ))
            }
            </StyledSelect>
          </Line>
          {
          panelType === 'Borrow'
            ? this.renderBorrowList()
            : this.renderRepayList()
        }
          {
            this.renderAmount()
          }
          <FooterComp
            onOk={this._confirm}
            onOkText={getLabel(`exchange-common-confirm-${panelType}`, i18n)}
            isLoading={isFetching}
            disable={disable}
            loadingText={getLabel('exchange-common-processing', i18n)}
          />
        </div>
      </Container>
    );
  }
}
